import { create } from 'zustand';
import { integrationService } from '../services/integration-service';
import { waitForSyncJob, SyncAbortedError, type HubspotSyncJobStatus } from '@/lib/hubspot-sync';
import type {
  IntegrationConfig,
  SyncLog,
  CreateIntegrationPayload,
} from '../types/integration.types';

interface IntegrationState {
  configs: IntegrationConfig[];
  logs: SyncLog[];
  loading: boolean;
  syncing: string | null; // id of currently syncing config
  error: string | null;

  fetchConfigs: () => Promise<void>;
  fetchAllLogs: () => Promise<void>;
  createConfig: (payload: CreateIntegrationPayload) => Promise<IntegrationConfig>;
  updateConfig: (id: string, payload: Partial<CreateIntegrationPayload>) => Promise<void>;
  deleteConfig: (id: string) => Promise<void>;
  testConnection: (id: string) => Promise<{ success: boolean; message: string }>;
  triggerSync: (id: string, direction: 'inbound' | 'outbound') => Promise<any>;
  /** HubSpot: encola, espera a que el worker termine y devuelve el resultado por entidad. */
  triggerHubspotSync: (
    id: string,
    opts?: { signal?: AbortSignal; onAlreadyRunning?: () => void }
  ) => Promise<HubspotSyncJobStatus>;
  hasActiveIntegration: (type: 'SAP_S4HANA' | 'SAP_C4C') => boolean;
}

export const useIntegrationStore = create<IntegrationState>((set, get) => ({
  configs: [],
  logs: [],
  loading: false,
  syncing: null,
  error: null,

  fetchConfigs: async () => {
    set({ loading: true, error: null });
    try {
      const configs = await integrationService.getAll();
      set({ configs, loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  fetchAllLogs: async () => {
    try {
      const { data } = await integrationService.getAllLogs(1, 50);
      set({ logs: data });
    } catch (error: any) {
      console.error('Error fetching logs:', error);
    }
  },

  createConfig: async (payload) => {
    const config = await integrationService.create(payload);
    set((state) => ({ configs: [...state.configs, config] }));
    return config;
  },

  updateConfig: async (id, payload) => {
    const updated = await integrationService.update(id, payload);
    set((state) => ({
      configs: state.configs.map((c) => (c.id === id ? updated : c)),
    }));
  },

  deleteConfig: async (id) => {
    await integrationService.delete(id);
    set((state) => ({
      configs: state.configs.map((c) => (c.id === id ? { ...c, isActive: false } : c)),
    }));
  },

  testConnection: async (id) => {
    return integrationService.testConnection(id);
  },

  triggerSync: async (id, direction) => {
    set({ syncing: id });
    try {
      const result = await integrationService.triggerSync(id, direction);
      // Refresh configs and logs
      await get().fetchConfigs();
      await get().fetchAllLogs();
      return result;
    } finally {
      set({ syncing: null });
    }
  },

  triggerHubspotSync: async (id, opts = {}) => {
    set({ syncing: id });
    try {
      const { jobId, alreadyRunning } = await integrationService.triggerHubspotSync(id);
      if (alreadyRunning && !opts.signal?.aborted) opts.onAlreadyRunning?.();
      const final = await waitForSyncJob(() => integrationService.getHubspotSyncJob(id, jobId), {
        signal: opts.signal,
      });
      if (opts.signal?.aborted) throw new SyncAbortedError();
      await get().fetchConfigs();
      await get().fetchAllLogs();
      return final;
    } finally {
      set({ syncing: null });
    }
  },

  hasActiveIntegration: (type) => {
    return get().configs.some((c) => c.type === type && c.isActive);
  },
}));
